import { faCircleEllipsis } from "@fortawesome/pro-regular-svg-icons/faCircleEllipsis";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Divider, IconButton, Menu, MenuItem } from "@mui/material";
import { fetchKnowledgeQuickAccess } from "app/features/menu/menuSlice";
import { store } from "app/store";
import { MprcConfirmDialog } from "components";
import { MprcFeedTypography } from "components/MprcFeedTypography";
import { FeedBlock, PublicationType } from "generated/CoreApi_v1";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { BlockDialog } from "views/knowledge/components/card/actions/BlockDialog";
import { ListPublicationsReorderItemsDialog } from "views/knowledge/components/card/actions/ListPublicationsReorderItemsDialog";
import { PublicationLogDialog } from "views/knowledge/components/card/actions/PublicationLogDialog";
import { PublicationRelationshipDialog } from "views/knowledge/components/card/actions/PublicationRelationshipDialog";
import { ReportErrorOrAbuseDialog } from "views/knowledge/components/card/actions/ReportErrorOrAbuseDialog";
import { DeletePublicationFactory } from "views/knowledge/components/card/behaviors/common/DeletePublicationFactory";
import { EditPublicationFactory } from "views/knowledge/components/card/behaviors/common/EditPublicationFactory";
import { useCardContext } from "views/knowledge/components/card/CardBuilder";
import { publish } from "views/knowledge/feed/FeedEvents";
import { classifyFactory, publicationFactory } from "views/knowledge/feed/FeedHelpers";

type ListMode = "default" | "content" | "myLists";

interface IProps {
    listMode: ListMode;
}

///
const MenuBaseOptions: React.FC<IProps> = (props) => {
    const navigate = useNavigate();
    const location = useLocation();

    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation("knowledge");
    const api = useCoreApi();
    const { listMode } = props;

    const {
        pub: {
            publicationId,
            publicationType,
            masterPublication,
            title,
            isDraft,
            readOnly,
            interaction: {
                allowDelete,
                allowBlock,
                allowEdit,
                allowClassify,
                allowViewHistory,
                allowCreateQuestion,
                allowRelatedContent,
                listInteraction,
                followingPublication
            },
            interaction,
            listDefinition,
        },
        onFeedItemChanged,
        onReload
    } = useCardContext();

    const [anchor, setAnchor] = React.useState<Element | null>(null);
    const [dialog, setDialog] = React.useState<any>(null);

    type MenuOptions =
        | "unfollow"
        | "openList"
        | "playback"
        | "edit"
        | "addItems"
        | "reorder"
        | "clear"
        | "classify"
        | "relatedContent"
        | "createTask"
        | "createQuestion"
        | "history"
        | "abuseOrError"
        | "block"
        | "delete"
        ;
    type MenuItem = { key: MenuOptions, label: string; disabled?: boolean; };

    const closeDialog = () => {
        setDialog(null);
    };

    const handleCloseClassify = (changed: boolean) => {
        if (changed) {
            onFeedItemChanged({
                interaction: { ...interaction, reclassified: changed }
            });
        }
        setDialog(null);
    };

    const groupOptions: Array<Array<MenuItem>> = [];

    const groupItems1: MenuItem[] = [];
    if (listMode != "content") groupItems1.push({ key: "openList", label: t("Feed.Card.Options.List.OpenListAction") });
    if (listDefinition!.itemCount > 0) groupItems1.push({ key: "playback", label: t("Feed.Card.Options.List.PlaybackAction") });
    if (groupItems1.length > 0) groupOptions.push(groupItems1);

    const groupItems2: MenuItem[] = [];
    if (allowEdit && (listMode == "content" || listMode == "myLists")) groupItems2.push({ key: "edit", label: t("Feed.Card.Options.List.Edit") });
    if (listMode == "myLists" && listDefinition?.isPublic === true && followingPublication) groupItems2.push({ key: "unfollow", label: t("Feed.Card.Options.List.UnfollowAction"), disabled: readOnly });
    if (listInteraction?.allowAddItems && listMode == "content") groupItems2.push({ key: "addItems", label: t("Feed.Card.Options.List.AddItemsAction") });
    if (listInteraction?.allowClearOrReorder && listMode == "content" && listDefinition!.itemCount > 0) groupItems2.push({ key: "reorder", label: t("Feed.Card.Options.List.ReorderAction") });
    if (listInteraction?.allowClearOrReorder && listMode == "content" && listDefinition!.itemCount > 0) groupItems2.push({ key: "clear", label: t("Feed.Card.Options.List.ClearAction") });
    if (allowClassify) groupItems2.push({ key: "classify", label: t("Feed.Card.Options.ClassifyAction") });
    if (allowRelatedContent && listMode != "myLists") groupItems2.push({ key: "relatedContent", label: t("Feed.Card.Options.RelatedContentAction") });
    if (groupItems2.length > 0) groupOptions.push(groupItems2);

    if (listMode != "myLists") {
        const groupItems3: MenuItem[] = [];
        if (allowCreateQuestion) groupItems3.push({ key: "createQuestion", label: t("Feed.Card.Options.CreateQuestionAction"), disabled: readOnly });
        if (groupItems3.length > 0) groupOptions.push(groupItems3);
    }

    const groupItems4: MenuItem[] = [];
    if (allowViewHistory) groupItems4.push({ key: "history", label: t("Feed.Card.Options.HistoryAction") });
    if (groupItems4.length > 0) groupOptions.push(groupItems4);

    const groupItems5: MenuItem[] = [];
    if (listMode != "myLists") {
        if (listDefinition?.isPublic === true) groupItems5.push({ key: "abuseOrError", label: t("Feed.Card.Options.AbuseOrReportAction"), disabled: readOnly });
        if (allowBlock) groupItems5.push({ key: "block", label: t("Feed.Card.Options.BlockAction"), disabled: readOnly });
    }
    if (allowDelete) groupItems5.push({ key: "delete", label: t("Feed.Card.Options.RemoveAction") });
    if (groupItems5.length > 0) groupOptions.push(groupItems5);


    // Opções do menu
    const menuItems = useMemo(() => {
        var arr: React.ReactNode[] = [];

        groupOptions.forEach((g, gindex) => {

            g.forEach((v) => {
                arr.push(<MenuItem
                    key={v.key}
                    disabled={v.disabled}
                    sx={(theme) => ({ color: v.key == "delete" ? theme.palette.error.main : undefined })}
                    onClick={
                        () => {
                            handleChooseMenuItem(v.key);
                            setAnchor(null);
                        }
                    }
                >
                    {v.label}
                </MenuItem>);
            });

            if (gindex < groupOptions.length - 1)
                arr.push(<Divider key={gindex} />);
        });

        return arr;
    }, [groupOptions]);

    ///
    const handleClosePublication = () => {
        setDialog(null);
    };

    ///
    const onReorderClose = (changed: boolean) => {
        setDialog(null);
        if (changed) {
            publish("ListReload", publicationId!);
        }
    };

    ///
    const clearList = () => {
        api.v1.knowledgePublication.clearListItems({ listPublicationId: publicationId })
            .then((result) => {
                if (result) {
                    publish("ListReload", publicationId);
                    enqueueSnackbar(t("Feed.Card.List.ClearAllSuccessMessage"), { variant: "success" });
                }
                else {
                    enqueueSnackbar(t("Feed.Card.List.ClearAllErrorMessage"), { variant: "error" });
                }
            });
    };

    ///
    const handleUnfollow = () => {
        api.v1.knowledgeSubcription.unsubscribePublication({ publicationId })
            .then((result) => {
                if (result) {
                    enqueueSnackbar(t("Publication.ListPlayback.UnfollowingMessage"), { variant: "success" });
                    store.dispatch(fetchKnowledgeQuickAccess({}));
                    onFeedItemChanged({
                        interaction: { ...interaction, followingPublication: false },
                    });
                }
            });
    };

    ///
    const handleChooseMenuItem = (option: MenuOptions) => {
        setAnchor(null);
        switch (option) {

            case "openList":
                navigate(document.location.search, { replace: true, state: { ...(location.state ?? {}), publicationId } });
                navigate(`${KnowledgePaths.ListPublicationViewer}/${publicationId}`);
                break;

            case "playback":
                navigate(document.location.search, { replace: true, state: { ...(location.state ?? {}), publicationId } });
                navigate(`${KnowledgePaths.ListPublicationInPlayback}/${publicationId}`);
                break;

            case "addItems":
                publish("ListAddItems", publicationId);
                break;

            case "unfollow":
                handleUnfollow();
                break;

            case 'reorder':
                setDialog(<ListPublicationsReorderItemsDialog
                    listPublicationId={publicationId}
                    title={title}
                    onClose={onReorderClose}
                />);
                break;

            case 'clear':
                setDialog(<MprcConfirmDialog
                    open
                    severity="danger"
                    message={<span>{t("Feed.Card.List.ConfirmClearAllMessage")}</span>}
                    onClose={
                        (result) => {
                            if (result) {
                                clearList();
                            }
                            setDialog(null);
                        }
                    }
                />);
                break;

            case "edit":
                setDialog(<EditPublicationFactory
                    publicationId={publicationId}
                    publicationType={publicationType}
                    masterPublicationId={masterPublication?.publicationId}
                    masterTitle={title}
                    onClose={(success) => { setDialog(null); if (success) onReload(); }}
                />);
                break;

            case "delete":
                setDialog(<DeletePublicationFactory
                    publicationId={publicationId}
                    title={title}
                    isDraft={isDraft}
                    publicationType={publicationType}
                    isThread={masterPublication != null}
                    onClose={
                        (deleted) => {
                            if (deleted) onFeedItemChanged({ deletedOn: new Date() });
                            setDialog(null);
                        }
                    }
                />);
                break;

            case "relatedContent":
                setDialog(<PublicationRelationshipDialog
                    publicationId={publicationId}
                    title={title}
                    onClose={
                        (changed) => {
                            if (changed) onFeedItemChanged({});
                            setDialog(null);
                        }
                    }
                />);
                break;

            case "history":
                setDialog(<PublicationLogDialog
                    publicationId={publicationId}
                    title={title}
                    onClose={closeDialog}
                />);
                break;

            case "classify":
                setDialog(classifyFactory(publicationId, publicationType, title, handleCloseClassify));
                break;

            case "block":
                setDialog(
                    <BlockDialog
                        onClose={
                            (blocked) => {
                                if (blocked) onFeedItemChanged({ blocked: { blockedOn: new Date() } as FeedBlock });
                                setDialog(null);
                            }
                        }
                        publicationId={publicationId}
                        title={title}
                    />);
                break;

            case "abuseOrError":
                setDialog(
                    <ReportErrorOrAbuseDialog
                        onClose={() => setDialog(null)}
                        publicationId={publicationId}
                        title={title}
                    />
                );
                break;

            case "createQuestion":
                setDialog(publicationFactory(null, PublicationType.Question, handleClosePublication,
                    {
                        mentions: [{
                            id: publicationId,
                            ot: "pub",
                            value: title,
                            denotationChar: "P"
                        }]
                    }));
                break;
        }
    };

    return (
        <>
            {dialog}
            <MprcFeedTypography variant="action">
                <IconButton color="inherit" onClick={(e) => setAnchor(e.currentTarget)}>
                    <FontAwesomeIcon icon={faCircleEllipsis} fixedWidth />
                </IconButton>
                <Menu
                    anchorEl={anchor}
                    open={anchor != null}
                    onClose={() => setAnchor(null)}
                >
                    {menuItems}
                </Menu>
            </MprcFeedTypography>
        </>
    );
};

export const MyListsOptions: React.FC = () => {
    return (
        <MenuBaseOptions listMode="myLists" />
    );
};


export const ContentOptions: React.FC = () => {
    return (
        <MenuBaseOptions listMode="content" />
    );
};

export const FeedOptions: React.FC = () => {
    return (
        <MenuBaseOptions listMode="default" />
    );
};
